import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  BIORHYTHM_CYCLE_LABELS,
  BIORHYTHM_PHASE_LABELS,
  COACHING_BIORHYTHM_DISCLAIMER,
  COACHING_RITUAL_MODULE_KEYS,
  computeBiorhythm,
  type CoachingRitualModule,
} from '@mlm/shared/coaching';
import { BreathRing } from '../components/breath-ring';
import { BREATH_SCALE } from '../lib/breath-scale';
import { FrequencyPlayer } from '../components/frequency-player';
import { linkButton } from '../components/panels/today';
import { ProLock } from '../components/pro-lock';
import {
  CoachButton,
  ErrorNote,
  Eyebrow,
  Field,
  GoldSeal,
  inputClass,
  Loading,
  Meter,
} from '../components/primitives';
import { StepFooter, StepRail } from '../components/step-rail';
import { coaching, type CoachingProfile, type Today } from '../lib/api';
import { minutesLabel } from '../lib/format';
import {
  qk,
  useFrequencies,
  useGuidance,
  useProfile,
  useRefreshCoaching,
  useToday,
  useFeatureLocked,
} from '../lib/queries';
import { morningSteps, type RitualStep } from '../lib/ritual';

/** SC-14: the morning ritual, one step at a time, paced to the minutes the member chose. */
export function MorningRitualPage() {
  const profile = useProfile();
  const today = useToday();
  if (profile.isPending || today.isPending) return <Loading />;
  if (profile.isError)
    return <ErrorNote error={profile.error} retry={() => void profile.refetch()} />;
  if (today.isError) return <ErrorNote error={today.error} retry={() => void today.refetch()} />;
  return <Ritual profile={profile.data.profile} today={today.data} />;
}

const isModule = (m: string): m is CoachingRitualModule =>
  (COACHING_RITUAL_MODULE_KEYS as readonly string[]).includes(m);

const headline = 'font-display text-[1.5rem] leading-snug text-ink lg:text-[1.875rem]';

function Ritual({ profile, today }: { profile: CoachingProfile; today: Today }) {
  const refresh = useRefreshCoaching();
  const rhythmLocked = useFeatureLocked('biorhythm');
  const listenLocked = useFeatureLocked('frequencies');
  const frequencies = useFrequencies();
  const hasBirthDate = Boolean(profile.birthDate);
  const steps = useMemo(
    () =>
      morningSteps(profile.ritualPrefs, {
        hasBirthDetails: profile.hasBirthDetails,
        hasBirthDate,
      }),
    [profile.ritualPrefs, profile.hasBirthDetails, hasBirthDate],
  );
  const minutes = profile.ritualPrefs.minutes ?? 5;
  const saved = today.ritual.morning;
  const [index, setIndex] = useState(() => {
    if (saved.completed) return steps.length;
    if (saved.started && saved.stepsTotal === steps.length) {
      return Math.min(saved.stepsCompleted, steps.length - 1);
    }
    return 0;
  });
  const [intention, setIntention] = useState(saved.intention ?? '');
  const [gratitude, setGratitude] = useState(['', '', '']);
  const [actionTitle, setActionTitle] = useState('');
  const announced = useRef(saved.started || saved.completed);

  useEffect(() => {
    if (announced.current) return;
    announced.current = true;
    coaching.event('morning_ritual_started', { steps: steps.length });
  }, [steps.length]);

  // A tall step (gratitude, the action) leaves the next one scrolled past its
  // own heading on a phone.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [index]);

  const primary = today.actions[0] ?? null;
  const gratitudeLines = gratitude.map((g) => g.trim()).filter(Boolean);
  const addAction = useMutation({
    mutationFn: (title: string) => coaching.createAction({ title, priority: 1 }),
  });
  const save = useMutation({
    mutationFn: (vars: { stepIndex: number; practice?: RitualStep['practice'] }) =>
      coaching.morningRitual({
        totalSteps: steps.length,
        stepsCompleted: vars.stepIndex + 1,
        completed: vars.stepIndex === steps.length - 1,
        modules: (profile.ritualPrefs.modules ?? []).filter(isModule),
        intention: intention.trim() || undefined,
        gratitude: gratitudeLines.length ? gratitudeLines : undefined,
        practices: vars.practice ? [vars.practice] : undefined,
      }),
  });

  if (index >= steps.length) return <RitualDone today={today} intention={intention} />;
  const step = steps[index]!;
  const last = index === steps.length - 1;
  const busy = save.isPending || addAction.isPending;
  const canContinue =
    step.key === 'intention'
      ? intention.trim().length > 0
      : step.key === 'gratitude'
        ? gratitudeLines.length > 0
        : true;

  // A tool step only counts when the member could actually use the tool: not
  // while it is Pro-only for them, and not a listening step with nothing to hear.
  const firstTrack = frequencies.data?.tracks[0] ?? null;
  const practiceFor = (s: RitualStep): RitualStep['practice'] => {
    if (s.key === 'rhythm' && rhythmLocked) return undefined;
    if (s.key === 'listen') {
      if (listenLocked) return undefined;
      if (!firstTrack) return undefined;
    }
    return s.practice;
  };

  const advance = async () => {
    const title = actionTitle.trim();
    if (step.key === 'action' && !primary && title.length >= 2) await addAction.mutateAsync(title);
    const result = await save.mutateAsync({ stepIndex: index, practice: practiceFor(step) });
    if (result.justCompleted) coaching.event('morning_ritual_completed', { steps: steps.length });
    if (last) void refresh();
    setIndex(index + 1);
  };

  const primaryMeta = primary
    ? [minutesLabel(primary.estimatedMinutes), today.primaryGoal?.title].filter(Boolean).join(' · ')
    : '';

  const body =
    step.key === 'energy' ? (
      <EnergyStep />
    ) : step.key === 'rhythm' ? (
      rhythmLocked ? (
        <ProLock feature="biorhythm" compact />
      ) : (
        <RhythmStep birthDate={profile.birthDate} day={today.day} />
      )
    ) : step.key === 'breathe' ? (
      <BreathCue />
    ) : step.key === 'listen' ? (
      listenLocked ? (
        <ProLock feature="frequencies" compact />
      ) : frequencies.isPending ? (
        <p className="eyebrow animate-pulse">Loading</p>
      ) : firstTrack ? (
        <div>
          <Eyebrow className="mb-2">{firstTrack.title}</Eyebrow>
          <FrequencyPlayer track={firstTrack} compact />
        </div>
      ) : (
        <p className="text-[0.95rem] leading-relaxed text-ink/70">
          No tracks are available yet. Sit quietly for a moment, then continue.
        </p>
      )
    ) : step.key === 'affirm' ? (
      <AffirmStep fallback={today.affirmation} />
    ) : step.key === 'visualize' ? (
      <div>
        <Eyebrow className="mb-2">Your goal</Eyebrow>
        <p className={headline}>{today.primaryGoal?.title ?? 'The life you are building'}</p>
        <p className="mt-4 text-[0.95rem] leading-relaxed text-ink/70">
          Close your eyes. See the moment it is real: where you are, who is with you, and how it
          feels.
        </p>
      </div>
    ) : step.key === 'gratitude' ? (
      <div className="space-y-2.5">
        {gratitude.map((value, i) => (
          <div key={i}>
            <label htmlFor={`ritual-gratitude-${i}`} className="sr-only">
              Gratitude {i + 1}
            </label>
            <input
              id={`ritual-gratitude-${i}`}
              className={inputClass}
              maxLength={200}
              value={value}
              placeholder={i === 0 ? 'I am grateful for' : 'And for'}
              onChange={(e) =>
                setGratitude(gratitude.map((g, j) => (j === i ? e.target.value : g)))
              }
            />
          </div>
        ))}
      </div>
    ) : step.key === 'intention' ? (
      <Field label="Today, I choose to be" htmlFor="ritual-intention">
        <input
          id="ritual-intention"
          className={inputClass}
          maxLength={280}
          value={intention}
          placeholder="Focused, patient and brave"
          onChange={(e) => setIntention(e.target.value)}
        />
      </Field>
    ) : primary ? (
      <div>
        <Eyebrow className="mb-2">Your #1 action today</Eyebrow>
        <p className={headline}>{primary.title}</p>
        {primaryMeta && <p className="eyebrow mt-2">{primaryMeta}</p>}
      </div>
    ) : (
      <Field
        label="Your most important action today"
        htmlFor="ritual-action"
        hint="It becomes today's #1 action."
      >
        <input
          id="ritual-action"
          className={inputClass}
          maxLength={200}
          value={actionTitle}
          placeholder="Finish the proposal draft"
          onChange={(e) => setActionTitle(e.target.value)}
        />
      </Field>
    );

  return (
    <div className="lg:grid lg:min-h-screen lg:grid-cols-[360px_minmax(0,1fr)]">
      <StepRail
        title="Morning ritual"
        steps={steps.map((s) => s.label)}
        index={index}
        note={`About ${minutes} minutes. Each step is saved as you finish it, so you can pick up where you left off.`}
      />
      <div className="mx-auto flex min-h-screen w-full max-w-xl flex-col px-5 pb-10 pt-6 lg:mx-0 lg:max-w-none lg:px-16 lg:pb-14 lg:pt-16 xl:px-[120px]">
        <div className="flex flex-1 flex-col lg:max-w-[640px]">
          <div className="mb-6 flex items-center justify-between lg:mb-10 lg:justify-end">
            <span className="font-display text-lg font-semibold text-ink lg:hidden">
              Miracles Protocol
            </span>
            <Link to="/home" className="eyebrow hover:text-ink">
              Close
            </Link>
          </div>
          <div className="mb-8 h-px w-full bg-[rgba(16,32,26,0.14)] lg:hidden">
            <div
              className="h-px bg-verde transition-[width] duration-300"
              style={{ width: `${((index + 1) / steps.length) * 100}%` }}
            />
          </div>
          <Eyebrow className="mb-3">
            Step {index + 1} of {steps.length}
          </Eyebrow>
          <h1 className="font-display text-[1.9rem] font-semibold leading-[1.12] text-ink lg:text-[2.5rem] lg:leading-[1.1]">
            {step.title}
          </h1>
          <p className="mt-3 text-[0.95rem] leading-relaxed text-ink/70 lg:text-base">
            {step.body}
          </p>
          <div className="mt-8">{body}</div>
          <StepTimer key={step.key} seconds={step.seconds} />
          <StepFooter progress={(index + 1) / steps.length}>
            <CoachButton
              kind="gold"
              block
              disabled={!canContinue || busy}
              onClick={() => void advance().catch(() => undefined)}
            >
              {last ? 'Begin my day' : 'Continue'}
            </CoachButton>
          </StepFooter>
        </div>
      </div>
    </div>
  );
}

function EnergyStep() {
  const guidance = useGuidance();
  if (guidance.isPending) return <p className="eyebrow animate-pulse">Loading</p>;
  if (guidance.isError || !guidance.data) {
    return (
      <p className="text-[0.95rem] leading-relaxed text-ink/70">
        Today's guidance is not available right now. Take a breath and continue.
      </p>
    );
  }
  const g = guidance.data;
  return (
    <div>
      <p className={headline}>{g.headline}</p>
      <p className="mt-3 text-[0.95rem] leading-relaxed text-ink/70">{g.summary}</p>
      {g.bestFor.length > 0 && (
        <p className="eyebrow mt-4">Best for: {g.bestFor.slice(0, 3).join(', ')}</p>
      )}
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{g.disclaimer}</p>
    </div>
  );
}

/** The member's own ritual affirmations when they chose some, else today's. */
function AffirmStep({ fallback }: { fallback: string }) {
  const list = useQuery({
    queryKey: qk.affirmations,
    queryFn: coaching.affirmations,
    staleTime: 60_000,
  });
  if (list.isPending) return <p className="eyebrow animate-pulse">Loading</p>;
  const chosen = (list.data ?? [])
    .filter((a) => a.inRitual)
    .slice(0, 3)
    .map((a) => a.text);
  return (
    <div className="space-y-5">
      {(chosen.length ? chosen : [fallback]).map((text) => (
        <p
          key={text}
          className="font-display text-[1.6rem] italic leading-snug text-ink lg:text-[2rem]"
        >
          {text}
        </p>
      ))}
    </div>
  );
}

const BREATH = [
  { cue: 'Breathe in', ms: 4000, scale: 1 },
  { cue: 'Hold', ms: 4000, scale: 1 },
  { cue: 'Breathe out', ms: 6000, scale: 0.55 },
] as const;

/** A slow 4-4-6 breathing pacer. With reduced motion the circle stays still and the cue still changes. */
function BreathCue() {
  const [phase, setPhase] = useState<number | null>(null);
  useEffect(() => {
    if (phase === null) {
      const frame = window.requestAnimationFrame(() => setPhase(0));
      return () => window.cancelAnimationFrame(frame);
    }
    const timer = window.setTimeout(() => setPhase((phase + 1) % BREATH.length), BREATH[phase]!.ms);
    return () => window.clearTimeout(timer);
  }, [phase]);
  const current = phase === null ? null : BREATH[phase]!;
  return (
    <BreathRing
      scale={current?.scale ?? BREATH_SCALE.out}
      durationMs={current?.ms ?? 0}
      cue={current?.cue ?? 'Breathe in'}
    />
  );
}

/** Today's cycles in brief, from the birth date alone. */
function RhythmStep({ birthDate, day }: { birthDate: string | null; day: string }) {
  const reading = computeBiorhythm(birthDate, day);
  if (!reading) {
    return (
      <p className="text-[0.95rem] leading-relaxed text-ink/70">
        Add your birth date in Settings to see your rhythm here.
      </p>
    );
  }
  const lead = reading.today[reading.strongest];
  return (
    <div>
      <p className={headline}>{lead.note}</p>
      <div className="mt-6 space-y-4">
        {reading.cycles.map((cycle) => (
          <Meter
            key={cycle}
            label={`${BIORHYTHM_CYCLE_LABELS[cycle]} · ${BIORHYTHM_PHASE_LABELS[reading.today[cycle].phase]}`}
            value={reading.today[cycle].level}
          />
        ))}
      </div>
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
        {COACHING_BIORHYTHM_DISCLAIMER}
      </p>
    </div>
  );
}

/** The step's suggested time. Advisory only: the member can continue whenever they are ready. */
function StepTimer({ seconds }: { seconds: number }) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    if (left <= 0) return;
    const timer = window.setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [left]);
  return (
    <div className="mt-10">
      <div className="flex items-baseline justify-between">
        <span className="eyebrow">
          {left > 0 ? 'Suggested time' : 'Continue when you are ready'}
        </span>
        <span className="font-mono text-sm tabular-nums text-sage">
          {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}
        </span>
      </div>
      <div className="mt-2 h-px w-full bg-[rgba(16,32,26,0.14)]">
        <div
          className="h-px origin-left bg-sage transition-transform duration-1000 ease-linear motion-reduce:transition-none"
          style={{ transform: `scaleX(${1 - left / seconds})` }}
        />
      </div>
    </div>
  );
}

function RitualDone({ today, intention }: { today: Today; intention: string }) {
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-xl flex-col px-5 pb-12 pt-6 lg:max-w-[640px] lg:pt-16">
      <span className="font-display text-lg font-semibold text-ink">Miracles Protocol</span>
      <div className="mt-16">
        <GoldSeal size={28} />
      </div>
      <h1 className="mt-5 font-display text-[1.9rem] font-semibold leading-[1.12] text-ink lg:text-[2.5rem] lg:leading-[1.1]">
        Your morning ritual is complete
      </h1>
      {intention.trim() && (
        <p className="mt-4 font-display text-[1.35rem] italic leading-snug text-ink/90">
          Today, I choose to be {intention.trim()}
        </p>
      )}
      <dl className="mt-8 border-t border-border">
        <div className="flex items-baseline justify-between gap-4 border-b border-border py-4">
          <dt className="eyebrow">Today's Success Score</dt>
          <dd className="text-[0.95rem] text-ink">
            {today.score.total} · {today.score.label}
          </dd>
        </div>
      </dl>
      <div className="mt-auto pt-10">
        <Link to="/home" className={linkButton}>
          Open my dashboard
        </Link>
      </div>
    </div>
  );
}
